import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { flushSync } from 'react-dom'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import {
  CreateAndUpdateBookingDriverDtoInput,
  useGetBookingAllocatedDriverQuery,
  useUpdateBookingDriverMutation,
} from 'generated/graphql'
import {
  GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
  GET_BOOKING_TOTAL_DRIVER_CHARGE,
  GET_BOOKING_ALLOCATED_DRIVERS,
} from 'graphql/queries'

import { EditDriverForm } from './EditDriverForm'

interface IProps {
  bookingId: number
  bookingDriverId: number
  openDialog: boolean
  handleCloseDialog: () => any
}

const EditDriverDialog = (props: IProps) => {
  const { bookingId, bookingDriverId, openDialog, handleCloseDialog } = props

  const methods = useForm<CreateAndUpdateBookingDriverDtoInput>({
    shouldUnregister: true,
  })

  const { handleSubmit, reset } = methods

  // Queries
  const { data: bookingDriverData, loading: bookingDriverLoading } =
    useGetBookingAllocatedDriverQuery({
      variables: { bookingDriverId },
      skip: !openDialog,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        flushSync(() => {
          if (response) reset({ ...response.bookingAllocatedDriver })
        })
      },
    })

  const driverId = bookingDriverData?.bookingAllocatedDriver?.driverId
  const parentDriverId =
    bookingDriverData?.bookingAllocatedDriver?.driver?.primaryDriverId || driverId

  // Mutations
  const [updateDriver, { loading: isUpdatingDriver }] = useUpdateBookingDriverMutation({
    refetchQueries: [
      {
        query: GET_PAGED_BOOKING_ALLOCATED_DRIVERS,
        variables: { bookingId: Number(bookingDriverData?.bookingAllocatedDriver?.bookingId) },
      },
      {
        query: GET_BOOKING_TOTAL_DRIVER_CHARGE,
        variables: { bookingId: Number(bookingDriverData?.bookingAllocatedDriver?.bookingId) },
      },
      {
        query: GET_BOOKING_ALLOCATED_DRIVERS,
        variables: { bookingId: Number(bookingDriverData?.bookingAllocatedDriver?.bookingId) },
      },
    ],
    onCompleted: (data) => {
      handleCloseDialog()

      toast.success(`Driver (${data.updateBookingDriver?.driver.name}) was updated with success.`)
    },
  })

  const handleOnSuccess: SubmitHandler<CreateAndUpdateBookingDriverDtoInput> = (formData) => {
    const {
      charge,
      extraPayment,
      xeroPurchaseCode,
      currencyId,
      isShownInstruction,
      instruction,
      driverId,
    } = formData
    const driver = bookingDriverData?.bookingAllocatedDriver

    if (driver) {
      updateDriver({
        variables: {
          input: {
            id: driver.id,
            bookingId: driver.bookingId,
            driverId: driverId || driver.driverId,
            vehicleId: driver.vehicleId,
            charge: Number(charge),
            extraPayment: Number(extraPayment),
            xeroPurchaseCode,
            currencyId,
            distance: 0, // ToDo: add distance
            isShownInstruction,
            instruction,
          },
        },
      })
    }
  }

  const handleClose = () => {
    reset({})
    handleCloseDialog()
  }

  const handleSubmitForm = () => {
    handleSubmit(handleOnSuccess)()
    reset({})
  }

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openDialog}
      aria-labelledby='edit-allocated-driver-dialog-title'>
      <FplDialogTitle id='edit-allocated-driver-dialog-title' onClose={handleClose}>
        <Typography mb={0} paragraph variant='h4'>
          Edit Allocated Driver
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          <form id='edit-allocated-driver-dialog-form' noValidate>
            <EditDriverForm
              key={`${driverId}-${parentDriverId}`}
              bookingId={bookingId}
              loading={bookingDriverLoading}
              driverId={driverId!}
              parentDriverId={parentDriverId!}
            />
          </form>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleClose}>
          Cancel
        </Button>

        <LoadingButton
          variant='contained'
          form='edit-allocated-driver-dialog-form'
          loading={isUpdatingDriver}
          onClick={handleSubmitForm}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditDriverDialog
